import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/Motion";
import { ShareButtons } from "@/components/ShareButtons";
import { buildMeta, buildLinks, breadcrumbSchema } from "@/lib/seo";
import { SITE, whatsappLink } from "@/lib/site";
import { POSTS } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = POSTS[params.slug];
    if (!post) throw notFound();
    return { post, slug: params.slug };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { post, slug } = loaderData;
    return {
      meta: buildMeta({
        title: `${post.title} | Blog Podomasso`,
        description: post.excerpt,
        path: `/blog/${slug}`,
        image: `${SITE.url}${post.image}`,
        type: "article",
      }),
      links: buildLinks(`/blog/${slug}`),
      scripts: [
        breadcrumbSchema([
          { name: "Início", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${slug}` },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center">
      <h1 className="font-display text-4xl">Artigo não encontrado</h1>
      <Button asChild variant="hero" className="mt-6">
        <Link to="/blog">Ver todos os artigos</Link>
      </Button>
    </div>
  ),
  component: PostPage,
});

function PostPage() {
  const { post } = Route.useLoaderData();
  return (
    <article className="bg-background">
      <div className="relative h-[50vh] overflow-hidden">
        <img
          src={post.image}
          alt={post.alt}
          className="h-full w-full object-cover"
          width={1600}
          height={1000}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="mx-auto -mt-32 max-w-3xl px-4 pb-20 sm:px-6 lg:px-8">
        <FadeUp>
          <div className="rounded-3xl bg-background p-8 shadow-elegant sm:p-12">
            <Link
              to="/blog"
              className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar ao blog
            </Link>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary">
              {post.category}
            </p>
            <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {post.readTime}
              </span>
              <span>•</span>
              <span>Por Rosa Chagas — Podóloga</span>
            </div>

            <ShareButtons 
              title={post.title} 
              path={`/blog/${Route.useLoaderData().slug}`} 
              className="mt-6 border-y border-border/50 py-4" 
            />

            <div className="mt-10 space-y-6">
              {post.paragraphs.map((p: { h?: string; p: string }, i: number) => (
                <div key={i}>
                  {p.h && (
                    <h2 className="mb-3 font-display text-2xl font-bold text-foreground">
                      {p.h}
                    </h2>
                  )}
                  <p className="text-base leading-relaxed text-foreground/85">{p.p}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 rounded-2xl bg-gradient-hero p-8 text-center text-primary-foreground">
              <h3 className="font-display text-2xl font-bold">Cuide dos seus pés hoje mesmo</h3>
              <p className="mx-auto mt-2 max-w-md opacity-90">
                Agende sua avaliação na Podomasso, em Belém do Pará.
              </p>
              <Button asChild variant="whatsapp" size="lg" className="mt-5">
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  Agendar pelo WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </FadeUp>
      </div>
    </article>
  );
}